/** Display-only ZAR — Stripe price IDs must match in env for checkout. */
/** Manager edition — yearly tuned ~23% below 12× monthly (749×12 = 8 988). */
export const MANAGER_MONTHLY_ZAR = 749;
export const MANAGER_YEARLY_ZAR = 6899;
export const OWNER_MONTHLY_ZAR = 1499;
export const OWNER_YEARLY_ZAR = 15999;

export type AnnualSavings = { ifPaidMonthly: number; savedZar: number; savedPct: number };

export function annualVsMonthlySavings(monthlyZar: number, yearlyZar: number): AnnualSavings {
  const ifPaidMonthly = monthlyZar * 12;
  const savedZar = Math.max(0, ifPaidMonthly - yearlyZar);
  const savedPct = ifPaidMonthly > 0 ? Math.round((savedZar / ifPaidMonthly) * 100) : 0;
  return { ifPaidMonthly, savedZar, savedPct };
}

/** Consistent rand display (en-ZA grouping). */
export function formatZarAmount(amount: number): string {
  return `R${amount.toLocaleString("en-ZA")}`;
}

/** Screen-reader sentence for annual vs monthly comparison. */
export function savingsAriaLabel(yearlyZar: number, save: Pick<AnnualSavings, "savedZar" | "savedPct">): string {
  return `Annual billing ${formatZarAmount(yearlyZar)} per year is about ${save.savedZar.toLocaleString("en-ZA")} rand less than twelve monthly payments — roughly ${save.savedPct} percent.`;
}

export const PRICING_FOOTNOTE_RENEWAL =
  "On monthly billing, plans renew every 30 days. Annual is one payment up front — same product, fewer invoices, less mental overhead.";

export function stripeHintManager(): string {
  return `Secure checkout via PayFast — ${formatZarAmount(MANAGER_MONTHLY_ZAR)} / month (recurring).`;
}

export function stripeHintOwner(): string {
  return `Secure checkout via PayFast — ${formatZarAmount(OWNER_MONTHLY_ZAR)} / month (recurring).`;
}

export const staffTierFeatures = [
  "20 batch scans — point, capture, move on",
  "Unlimited manual add & edit when the shelf changes faster than the scanner",
  "1 handwriting / stock-sheet scan to bridge paper and system",
  "1 bundled email — summary, reminder, and a nudge you can act on",
  "1 pharmacy profile — learn the rhythm before the volume arrives",
] as const;

export const managerTierFeatures = [
  "100 batch scans per cycle — room for a busy week without rationing",
  "10 handwriting / stock-sheet scans when the delivery note beats the keyboard",
  "Twice-weekly email — alerts, digest, and inventory angles pulled from your live data",
  "Full scan history — write-offs, transfers & dispensed lines in one honest thread",
  "Up to 2 pharmacies or storerooms (two branches or SLO1 & SLO2) — one trail, so “where is it?” has an answer",
] as const;

export const ownerTierFeatures = [
  "Unlimited batch scans — no ceiling on capture days",
  "Unlimited handwriting & batch capture for high-touch receiving",
  "In-app notifications & stock insights that meet people where they work",
  "Twice-weekly email recap — a steady pulse without another meeting",
  "Pill Counter included — powered by Anthropic",
  "Up to 350 profiles / stores — one dashboard; you decide what earns attention",
  "Roles: staff capture & edit quantities (no pricing); managers audit, price, and own the record",
] as const;
